import { useEffect, useRef, useState } from 'react';
import { App, Button, Card, Form, Input, Typography } from 'antd';
import type { InputRef } from 'antd';
import { MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiErrorCode, apiErrorMessage } from '@/api/client';
import { sendLoginCode, verifyLoginCode } from '@/api/auth';
import { useSessionStore } from '@/store/session';

const { Paragraph, Title, Text } = Typography;

/** 重新发送验证码的冷却秒数。与后端 code.ts 的 RESEND_COOLDOWN_MS 保持一致。 */
const RESEND_SECONDS = 60;

/**
 * 登录页。两步：填邮箱拿码 → 填码换会话。没有密码，也没有注册入口 ——
 * 账号只能由管理员在「成员」页拉进来。
 */
export default function Login() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSessionStore((s) => s.status);
  const signIn = useSessionStore((s) => s.signIn);

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // antd 的 Input 暴露的是自己的 InputRef（含 focus/blur/select），不是原生 HTMLInputElement
  const codeInputRef = useRef<InputRef>(null);

  // 冷却倒计时。清理函数不能省：组件在倒计时途中卸载时，定时器会继续对已卸载组件 setState。
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // 进入第二步后自动聚焦验证码框，省一次点击
  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  // 已登录的人不该再看到登录页
  if (status === 'authenticated') return <Navigate to="/" replace />;

  async function requestCode() {
    if (!email.trim()) {
      message.warning('请输入邮箱');
      return;
    }
    setSending(true);
    try {
      await sendLoginCode(email.trim());
      setStep('code');
      setCooldown(RESEND_SECONDS);
      message.success('验证码已发送，请查收邮件');
    } catch (err) {
      // NOT_INVITED 是本系统最常见的一种「登不进去」，文案必须说清下一步找谁，
      // 否则用户只会反复重试同一个邮箱。
      if (apiErrorCode(err) === 'NOT_INVITED') {
        message.error('这个邮箱还没有被加入组织，请联系管理员把你拉进来');
      } else {
        message.error(apiErrorMessage(err));
      }
    } finally {
      setSending(false);
    }
  }

  async function submitCode() {
    if (code.length !== 6) {
      message.warning('请输入 6 位验证码');
      return;
    }
    setVerifying(true);
    try {
      const res = await verifyLoginCode(email.trim(), code);
      signIn(res.token, { user: res.user, organization: res.organization, roles: res.roles });
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      message.error(apiErrorMessage(err));
      setCode('');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 400 }}>
        <Title level={3} style={{ marginTop: 0 }}>
          hiPay
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {step === 'email'
            ? '输入你的工作邮箱，我们会发一封验证码邮件。'
            : `验证码已发送至 ${email}`}
        </Paragraph>

        {step === 'email' ? (
          <Form layout="vertical" onFinish={requestCode}>
            <Form.Item label="邮箱">
              <Input
                autoFocus
                size="large"
                prefix={<MailOutlined />}
                placeholder="you@lessie.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onPressEnter={requestCode}
              />
            </Form.Item>
            <Button type="primary" size="large" block loading={sending} onClick={requestCode}>
              获取验证码
            </Button>
          </Form>
        ) : (
          <Form layout="vertical" onFinish={submitCode}>
            <Form.Item label="验证码">
              <Input
                ref={codeInputRef}
                size="large"
                prefix={<SafetyCertificateOutlined />}
                placeholder="6 位数字"
                maxLength={6}
                value={code}
                // 只留数字：手机输入法和粘贴很容易带进空格或连字符
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onPressEnter={submitCode}
              />
            </Form.Item>
            <Button type="primary" size="large" block loading={verifying} onClick={submitCode}>
              登录
            </Button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setStep('email')}>
                换个邮箱
              </Button>
              <Button type="link" size="small" disabled={cooldown > 0 || sending} onClick={requestCode}>
                {cooldown > 0 ? `${cooldown} 秒后可重发` : '重新发送'}
              </Button>
            </div>
          </Form>
        )}

        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 24 }}>
          暂不开放自助注册。需要由组织管理员把你加入后才能登录。
        </Text>
      </Card>
    </div>
  );
}
